import { trackToolAction } from "../../../utils/analytics";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { apiService } from "../../../api/api";
import type { IncorrectAssetTypesAudit } from "../../../utils/@types";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import MultiSearch from "../../../Components/SearchInputs/MultiSearch/MultiSearch";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import "../../YouTubeScorecard/Components/Tables/tables.css";
import { Card } from "primereact/card";
import {
  buildQueryString,
  parseBooleanParam,
  parseStringArrayParam,
  parseStringParam,
} from "../../../utils/urlFilters";
import { FULL_PAGE_TABLE_SCROLL_HEIGHT } from "../../../config/tableLayout";
import { withExportDate } from "../../../utils/exportFileName";

interface ColumnConfig {
  field: string;
  header: string;
  sortable?: boolean;
  visible: boolean;
}

const FilterBar = ({
  filters,
  setFilters,
  unfilteredData,
  onSearch,
  onReset,
  onExport,
  onColumnVisibility,
}: any) => {
  // Standard filter options
  const brandOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.brand))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const legalEntityOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.legalEntity))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const legalEntityTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.legalEntityType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  const assetTypeOptions = Array.from(
    new Set(unfilteredData.map((item: any) => item.assetType))
  )
    .filter(Boolean)
    .map((value) => ({ label: String(value), value }));

  return (
    <>
      <div className="grid-container" style={{ marginTop: "-1rem" }}>
        <div className="filter-item">
          <MultiSearch
            selectedItems={filters.videoId}
            value={(items) =>
              setFilters((prev: any) => ({ ...prev, videoId: items }))
            }
            label={"Video ID"}
            parseType="both"
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Brand
          </text>
          <MultiSelect
            value={filters.brand}
            options={brandOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, brand: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Asset Type
          </text>
          <MultiSelect
            value={filters.assetType}
            options={assetTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({ ...prev, assetType: e.value }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Legal Entity
          </text>
          <MultiSelect
            value={filters.legalEntity ? [filters.legalEntity] : []}
            options={legalEntityOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntity: e.value[0] || "",
              }))
            }
            maxSelectedLabels={1}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <text
            style={{ color: "grey", fontSize: "small", marginLeft: "0.4rem" }}
          >
            Legal Entity Type
          </text>
          <MultiSelect
            value={filters.legalEntityType}
            options={legalEntityTypeOptions}
            onChange={(e) =>
              setFilters((prev: any) => ({
                ...prev,
                legalEntityType: e.value,
              }))
            }
            maxSelectedLabels={3}
            optionLabel="label"
            style={{ width: "90%", borderRadius: "5px" }}
            filter
            showClear
          />
        </div>
        <div className="filter-item">
          <div
            className="flex align-items-center gap-2"
            style={{ marginTop: "1.5rem" }}
          >
            <Checkbox
              checked={filters.highPriority || false}
              onChange={(e) =>
                setFilters((prev: any) => ({
                  ...prev,
                  highPriority: e.checked || false,
                }))
              }
            />
            <label className="text-sm font-medium" style={{ color: "grey" }}>
              High Priority Only
            </label>
          </div>
        </div>
      </div>
      <div className="grid-container">
        <div className="flex-row grid gap-5" style={{ marginLeft: "0.1vh" }}>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button label="Search" icon="pi pi-search" onClick={onSearch} />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Reset"
              icon="pi pi-replay"
              severity="danger"
              onClick={onReset}
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Export"
              icon="pi pi-file"
              onClick={onExport}
              severity="success"
            />
          </div>
          <div className="flex-2" style={{ paddingTop: "1.5rem" }}>
            <Button
              label="Columns"
              icon="pi pi-columns"
              onClick={onColumnVisibility}
              severity="secondary"
            />
          </div>
        </div>
      </div>
    </>
  );
};

const IncorrectAssetTypesAuditComponent: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilters = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      videoId: parseStringArrayParam(params, "videoId"),
      brand: parseStringArrayParam(params, "brand"),
      assetType: parseStringArrayParam(params, "assetType"),
      legalEntity: parseStringParam(params, "legalEntity"),
      legalEntityType: parseStringArrayParam(params, "legalEntityType"),
      highPriority: parseBooleanParam(params, "highPriority") || false,
    };
  }, [location.search]);

  const dt = useRef<DataTable<IncorrectAssetTypesAudit[]>>(null);
  const toast = useRef<Toast>(null);
  const initialFiltersApplied = useRef(false);
  const [rows, setRows] = useState<IncorrectAssetTypesAudit[]>([]);
  const [filters, setFilters] = useState<any>(initialFilters);
  const [unfilteredData, setUnfilteredData] = useState<
    IncorrectAssetTypesAudit[]
  >([]);
  const [filteredRows, setFilteredRows] = useState<IncorrectAssetTypesAudit[]>(
    []
  );
  const [columnVisibilityDialog, setColumnVisibilityDialog] = useState(false);
  const [selectedRows, setSelectedRows] = useState<IncorrectAssetTypesAudit[]>(
    []
  );

  // Column configuration for asset types audit
  const [columnConfig, setColumnConfig] = useState<ColumnConfig[]>([
    { field: "isHighPriority", header: "Priority", visible: true },
    { field: "videoId", header: "Video ID", sortable: true, visible: true },
    { field: "title", header: "Title", sortable: true, visible: true },
    { field: "assetId", header: "Asset ID", sortable: true, visible: true },
    { field: "customId", header: "Custom ID", sortable: true, visible: true },
    { field: "assetType", header: "Asset Type", sortable: true, visible: true },
    { field: "videoType", header: "Video Type", sortable: true, visible: true },
    { field: "artist", header: "Artist", sortable: true, visible: true },
    { field: "legalEntity", header: "Legal Entity", sortable: true, visible: true },
    { field: "legalEntityType", header: "Legal Entity Type", sortable: true, visible: true },
    {
      field: "pointOfFailure",
      header: "Point of Failure",
      sortable: true,
      visible: true,
    },
    {
      field: "approxDailyViews",
      header: "Approx Daily Views",
      sortable: true,
      visible: true,
    },
    { field: "brand", header: "Brand", sortable: true, visible: true },
    {
      field: "contentOwner",
      header: "Content Owner",
      sortable: true,
      visible: true,
    },
    { field: "checked", header: "Checked", visible: true },
    { field: "notes", header: "Notes", visible: true },
  ]);

  useEffect(() => {
    apiService.musicAssetAudit.getIncorrectAssetTypes({}).then((data: any) => {
      setUnfilteredData(data);
      setRows(data);
      setFilteredRows(data);
    });
  }, []);

  const isHighPriorityRow = (row: IncorrectAssetTypesAudit): boolean =>
    row.isHighPriority || false;

  const handleSearch = useCallback(() => {
    trackToolAction("DataHealthTool", "search");
    let filtered = rows;
    if (filters.videoId?.length)
      filtered = filtered.filter((r) =>
        filters.videoId.some((v: string) =>
          r.videoId?.toLowerCase().includes(v.toLowerCase())
        )
      );
    if (filters.brand?.length)
      filtered = filtered.filter((r) => filters.brand.includes(r.brand));
    if (filters.assetType?.length)
      filtered = filtered.filter((r) => filters.assetType.includes(r.assetType));
    if (filters.legalEntity)
      filtered = filtered.filter((r) => r.legalEntity === filters.legalEntity);
    if (filters.legalEntityType?.length)
      filtered = filtered.filter((r) =>
        filters.legalEntityType.includes(r.legalEntityType)
      );
    if (filters.highPriority)
      filtered = filtered.filter((r) => r.isHighPriority === true);
    setFilteredRows(filtered);
    const query = buildQueryString({
      videoId: filters.videoId,
      brand: filters.brand,
      assetType: filters.assetType,
      legalEntity: filters.legalEntity,
      legalEntityType: filters.legalEntityType,
      highPriority: filters.highPriority,
    });
    navigate({ search: query }, { replace: true });
  }, [filters, navigate, rows]);

  const handleReset = () => {
    setFilters({
      videoId: [],
      brand: [],
      assetType: [],
      legalEntity: "",
      legalEntityType: [],
      highPriority: false,
    });
    setFilteredRows(rows);
    navigate({ search: "" }, { replace: true });
  };

  useEffect(() => {
    const hasInitialFilters = Object.values(initialFilters).some((value) => {
      if (typeof value === "boolean") return value;
      if (Array.isArray(value)) return value.length > 0;
      return Boolean(value);
    });
    if (!hasInitialFilters || rows.length === 0 || initialFiltersApplied.current)
      return;
    handleSearch();
    initialFiltersApplied.current = true;
  }, [initialFilters, rows, handleSearch]);

  const onRowEditComplete = (e: any) => {
    let _rows = [...rows];
    let { newData, index } = e;
    _rows[index] = newData;
    setRows(_rows);
    apiService.musicAssetAudit.updateIncorrectAssetTypes(newData);
  };

  const checkedBodyTemplate = (rowData: IncorrectAssetTypesAudit) => (
    <Checkbox
      onChange={(e) => {
        let _rows = [...rows];
        const index = _rows.findIndex((r) => r.videoId === rowData.videoId);
        _rows[index].checked = e.checked;
        setRows(_rows);
        apiService.musicAssetAudit.updateIncorrectAssetTypes({
          videoId: rowData.videoId,
          checked: e.checked,
        });
      }}
      checked={rowData.checked ?? false}
    />
  );

  const notesEditor = (options: any) => (
    <InputText
      type="text"
      value={options.value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
        options.editorCallback && options.editorCallback(e.target.value)
      }
    />
  );

  const rowClassName = (data: IncorrectAssetTypesAudit) => ({
    "bg-blue-100": data.isNew,
    "bg-red-100 border-l-4 border-red-500": isHighPriorityRow(data),
  });

  const priorityBodyTemplate = (rowData: IncorrectAssetTypesAudit) => {
    const isHighPriority = isHighPriorityRow(rowData);
    const getPriorityText = () => {
      if (rowData.priorityReason) {
        const reasons = {
          high_views: "High Views",
          priority_channel: "Priority Channel",
          new_asset_code: "New Asset Code",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };
    const getPriorityTooltip = () => {
      if (rowData.priorityReason) {
        const reasons = {
          high_views: "High Daily Views (>25,000)",
          priority_channel: "Priority Channel (Uploaded in last 30 days)",
          new_asset_code: "New Asset Code (Uploaded in last 14 days)",
        };
        return (
          reasons[rowData.priorityReason as keyof typeof reasons] ||
          rowData.priorityReason
        );
      }
      return "";
    };

    return (
      <div className="flex align-items-center gap-2">
        {rowData.isPriority && (
          <span
            className="text-yellow-600 font-semibold text-xs"
            title="Manual Priority"
          >
            Manual
          </span>
        )}
        {isHighPriority && (
          <span
            className="text-red-600 font-semibold text-xs px-2 py-1 bg-red-100 rounded"
            title={getPriorityTooltip()}
          >
            {getPriorityText()}
          </span>
        )}
      </div>
    );
  };

  const exportCSV = () => {
    trackToolAction("DataHealthTool", "export");
    dt?.current?.exportCSV();
  };

  const handleColumnVisibilityChange = (field: string, visible: boolean) => {
    setColumnConfig((prev) =>
      prev.map((col) => (col.field === field ? { ...col, visible } : col))
    );
  };

  const resetColumnVisibility = () => {
    setColumnConfig((prev) => prev.map((col) => ({ ...col, visible: true })));
  };

  const isColumnVisible = (field: string): boolean => {
    const column = columnConfig.find((col) => col.field === field);
    const result = column?.visible === true || false;
    return result;
  };

  const calculateSummaryMetrics = () => {
    const totalIssues = filteredRows.length;
    const uniqueAssetIds = new Set(filteredRows.map((row) => row.assetId)).size;

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    const newIssues = filteredRows.filter((row) => {
      if (!row.createdAt) return false;
      return new Date(row.createdAt) >= sevenDaysAgo;
    }).length;

    const totalApproxDailyViews = filteredRows.reduce(
      (sum, row) => sum + (row.approxDailyViews || 0),
      0
    );

    return { totalIssues, uniqueAssetIds, newIssues, totalApproxDailyViews };
  };

  const summaryMetrics = calculateSummaryMetrics();

  const handleCellClick = (event: any) => {
    const cellValue = event.value;
    if (cellValue && String(cellValue).trim()) {
      const valueToCopy = String(cellValue).trim();
      navigator.clipboard
        .writeText(valueToCopy)
        .then(() => {
          toast.current?.show({
            severity: "success",
            summary: "Copied!",
            detail: `"${valueToCopy}" copied to clipboard`,
            life: 2000,
          });
        })
        .catch(() => {
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to copy to clipboard",
            life: 2000,
          });
        });
    }
  };

  const getCorrectAssetType = (currentAssetType: string): string => {
    // For music videos, the correct asset type should typically be 'music_video'
    // If it's already 'music_video', then it might need to be something else
    if (
      currentAssetType === "music_video" ||
      currentAssetType === "MUSIC_VIDEO"
    ) {
      return "web"; // Fallback to generic video if it's already music_video
    }

    // Most incorrect asset types should be changed to 'music_video'
    return "music_video";
  };

  const handleUpdateAssetType = async (rowData: IncorrectAssetTypesAudit) => {
    try {
      if (!rowData.assetId || !rowData.assetType) {
        toast.current?.show({
          severity: "error",
          summary: "Missing Data",
          detail: "Asset ID or Asset Type is missing",
          life: 3000,
        });
        return;
      }

      const correctAssetType = getCorrectAssetType(rowData.assetType);

      const updateData = {
        assetId: rowData.assetId,
        assetType: correctAssetType,
        contentOwner: rowData.contentOwner || rowData.brand || "unknown",
      };

      await apiService.musicAssetAudit.pushIncorrectAssetTypesUpdateTask([
        updateData,
      ]);

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `Asset type will be updated from "${rowData.assetType}" to "${correctAssetType}" for ${rowData.videoId}`,
        life: 3000,
      });
    } catch (error) {
      console.error("Error updating asset type:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to queue asset type update task",
        life: 3000,
      });
    }
  };

  const handleBulkUpdateAssetTypes = async () => {
    if (selectedRows.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No Selection",
        detail: "Please select rows to update",
        life: 3000,
      });
      return;
    }

    try {
      const validRows = selectedRows.filter(
        (row) => row.assetId && row.assetType
      );

      if (validRows.length === 0) {
        toast.current?.show({
          severity: "warn",
          summary: "No Valid Data",
          detail:
            "Selected rows are missing required Asset ID or Asset Type data",
          life: 3000,
        });
        return;
      }

      const updateData = validRows.map((row) => ({
        assetId: row.assetId!,
        assetType: getCorrectAssetType(row.assetType!),
        contentOwner: row.contentOwner || row.brand || "unknown",
      }));

      await apiService.musicAssetAudit.pushIncorrectAssetTypesUpdateTask(
        updateData
      );

      toast.current?.show({
        severity: "success",
        summary: "Success",
        detail: `Queued ${validRows.length} asset type update tasks to correct asset types`,
        life: 3000,
      });

      setSelectedRows([]);
    } catch (error) {
      console.error("Error bulk updating asset types:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to queue bulk asset type update tasks",
        life: 3000,
      });
    }
  };

  const actionBodyTemplate = (rowData: IncorrectAssetTypesAudit) => (
    <div className="flex gap-2">
      <Button
        icon="pi pi-sync"
        size="small"
        severity="info"
        tooltip="Update Asset Type"
        onClick={() => handleUpdateAssetType(rowData)}
      />
    </div>
  );

  return (
    <div style={{ width: "100%", marginTop: "-30px", marginBottom: "-30px" }}>
      <Toast ref={toast} />
      <Dialog
        header="Column Visibility"
        visible={columnVisibilityDialog}
        style={{ width: "40vw" }}
        onHide={() => setColumnVisibilityDialog(false)}
      >
        <div className="grid">
          {columnConfig.map((column) => (
            <div key={column.field} className="col-12 md:col-6 lg:col-4">
              <div className="flex align-items-center gap-2 p-2">
                <Checkbox
                  checked={column.visible}
                  onChange={(e) =>
                    handleColumnVisibilityChange(
                      column.field,
                      e.checked || false
                    )
                  }
                />
                <label className="text-sm">{column.header}</label>
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-content-end gap-2 mt-3">
          <Button
            label="Reset All"
            icon="pi pi-refresh"
            onClick={resetColumnVisibility}
            severity="secondary"
            outlined
          />
          <Button
            label="Close"
            icon="pi pi-times"
            onClick={() => setColumnVisibilityDialog(false)}
          />
        </div>
      </Dialog>

      {/* Summary metrics card */}
      <Card
        className="mb-3"
        style={{
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <div className="grid">
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-blue-600">
                {summaryMetrics.totalIssues.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">
                Total Incorrect Asset Types
              </div>
            </div>
          </div>
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-green-600">
                {summaryMetrics.uniqueAssetIds.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Unique Asset IDs</div>
            </div>
          </div>
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-orange-600">
                {summaryMetrics.newIssues.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">New Issues (7 days)</div>
            </div>
          </div>
          <div className="col-12 md:col-3">
            <div className="text-center p-3">
              <div className="text-2xl font-bold text-purple-600">
                {summaryMetrics.totalApproxDailyViews.toLocaleString()}
              </div>
              <div className="text-sm text-gray-600">Total Daily Views</div>
            </div>
          </div>
        </div>
      </Card>

      <Card
        className="flex-column md:flex-col gap-3"
        style={{
          marginTop: "0.3rem",
          marginBottom: "2rem",
          boxShadow: "0px 4px 11px rgba(0, 0, 0, 0.1)",
          borderRadius: "7px",
        }}
      >
        <FilterBar
          filters={filters}
          setFilters={setFilters}
          unfilteredData={unfilteredData}
          onSearch={handleSearch}
          onReset={handleReset}
          onExport={exportCSV}
          onColumnVisibility={() => setColumnVisibilityDialog(true)}
        />

        {/* Bulk Update Button */}
        <div className="flex justify-content-end mb-3">
          <Button
            label={`Update Selected (${selectedRows.length})`}
            icon="pi pi-sync"
            severity="success"
            disabled={selectedRows.length === 0}
            onClick={handleBulkUpdateAssetTypes}
          />
        </div>

        <DataTable
          ref={dt}
          exportFilename={withExportDate("Asset Type Audit")}
          value={filteredRows}
          size="normal"
          paginator
          rows={50}
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
          showGridlines
          cellSelection
          stripedRows
          removableSort
          scrollable
          scrollHeight={FULL_PAGE_TABLE_SCROLL_HEIGHT}
          sortMode="multiple"
          selectionMode="multiple"
          selection={selectedRows}
          onSelectionChange={(e: { value: IncorrectAssetTypesAudit[] }) =>
            setSelectedRows(e.value)
          }
          tableStyle={{ width: "auto", fontSize: "0.8rem" }}
          className="custom-datatable-paginator"
          editMode="row"
          onRowEditComplete={onRowEditComplete}
          onCellClick={handleCellClick}
          rowClassName={rowClassName}
        >
          {isColumnVisible("videoId") && (
            <Column field="videoId" header="Video ID" sortable />
          )}
          {isColumnVisible("title") && (
            <Column field="title" header="Title" sortable />
          )}
          {isColumnVisible("assetId") && (
            <Column field="assetId" header="Asset ID" sortable />
          )}
          {isColumnVisible("customId") && (
            <Column field="customId" header="Custom ID" sortable />
          )}
          {isColumnVisible("assetType") && (
            <Column field="assetType" header="Asset Type" sortable />
          )}
          {isColumnVisible("videoType") && (
            <Column field="videoType" header="Video Type" sortable />
          )}
          {isColumnVisible("artist") && (
            <Column field="artist" header="Artist" sortable />
          )}
          {isColumnVisible("legalEntity") && (
            <Column field="legalEntity" header="Legal Entity" sortable />
          )}
          {isColumnVisible("legalEntityType") && (
            <Column field="legalEntityType" header="Legal Entity Type" sortable />
          )}
          {isColumnVisible("pointOfFailure") && (
            <Column field="pointOfFailure" header="Point of Failure" sortable />
          )}
          {isColumnVisible("approxDailyViews") && (
            <Column
              field="approxDailyViews"
              header="Approx Daily Views"
              body={(rowData) =>
                rowData.approxDailyViews?.toLocaleString() || "0"
              }
              sortable
            />
          )}
          {isColumnVisible("brand") && (
            <Column field="brand" header="Brand" sortable />
          )}
          {isColumnVisible("contentOwner") && (
            <Column field="contentOwner" header="Content Owner" sortable />
          )}
          {isColumnVisible("isHighPriority") && (
            <Column
              body={priorityBodyTemplate}
              header="Priority"
              sortable
              field="isHighPriority"
            />
          )}
          <Column
            rowEditor
            headerStyle={{ width: "10%", minWidth: "8rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          <Column
            body={actionBodyTemplate}
            header="Actions"
            headerStyle={{ width: "10%", minWidth: "8rem" }}
            bodyStyle={{ textAlign: "center" }}
          />
          {isColumnVisible("checked") && (
            <Column body={checkedBodyTemplate} header="Checked" />
          )}
          {isColumnVisible("notes") && (
            <Column
              field="notes"
              header="Notes"
              editor={notesEditor}
              sortable
            />
          )}
        </DataTable>
      </Card>
    </div>
  );
};

export default IncorrectAssetTypesAuditComponent;
